import type {
  Holding,
  HoldingsImportResult,
  MappingCandidate,
  MappingException,
  MappingExceptionResolve,
  Page,
  Portfolio,
} from '@fruuit/shared'
import { keepPreviousData } from '@tanstack/react-query'
import { ApiError, api, buildUrl, CSRF_COOKIE, newRequestId, readCookie } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

export function useHoldings(
  org: string | null,
  options: { cursor: string | null; query?: string; mapping?: string },
) {
  return useApiQuery<Page<Holding>>(['holdings', org, options], '/holdings', {
    query: {
      org,
      cursor: options.cursor,
      query: options.query,
      mapping: options.mapping,
      limit: 50,
    },
    placeholderData: keepPreviousData,
  })
}

export function usePortfolios(org: string | null) {
  return useApiQuery<{ items: Portfolio[] }>(['portfolios', org], '/portfolios', { query: { org } })
}

export function useMappingExceptions(org: string | null, status: string, cursor: string | null) {
  return useApiQuery<Page<MappingException>>(
    ['mapping-exceptions', org, status, cursor],
    '/mapping-exceptions',
    {
      query: { org, status: status || undefined, cursor, limit: 50 },
      placeholderData: keepPreviousData,
    },
  )
}

export function useMappingCandidates(org: string | null, exceptionId: string | null, q: string) {
  return useApiQuery<{ items: MappingCandidate[] }>(
    ['mapping-candidates', org, exceptionId, q],
    `/mapping-exceptions/${exceptionId ?? ''}/candidates`,
    { query: { org, q: q || undefined }, enabled: Boolean(exceptionId) },
  )
}

export function useResolveException(org: string | null) {
  return useApiMutation(
    (vars: { id: string; body: MappingExceptionResolve }, { requestId }) =>
      api.post<MappingException>(`/mapping-exceptions/${vars.id}/resolve`, vars.body, {
        requestId,
        query: { org },
      }),
    { invalidate: [['mapping-exceptions'], ['coverage'], ['holdings'], ['dashboard']] },
  )
}

/**
 * POST the CSV as `text/csv` (the JSON client can't send raw text). Same
 * request-id, CSRF and error contract as `apiFetch`.
 */
export async function uploadHoldingsCsv(
  file: { text: string },
  options: { org: string | null; portfolioId?: string; dryRun?: boolean; requestId?: string },
): Promise<HoldingsImportResult> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'text/csv',
    'X-Request-Id': options.requestId ?? newRequestId(),
  }
  const csrf = readCookie(CSRF_COOKIE)
  if (csrf) headers['X-CSRF-Token'] = csrf
  const response = await fetch(
    buildUrl('/holdings/import', {
      org: options.org,
      portfolio_id: options.portfolioId,
      dry_run: options.dryRun ? 'true' : undefined,
    }),
    { method: 'POST', headers, body: file.text, credentials: 'include' },
  )
  const text = await response.text()
  const body = text ? (JSON.parse(text) as unknown) : undefined
  if (!response.ok) {
    const error = (
      body as { error?: { code: string; message: string; request_id?: string; details?: unknown } }
    )?.error
    throw new ApiError({
      status: response.status,
      code: error?.code ?? 'unexpected_response',
      message: error?.message ?? `The upload failed (HTTP ${response.status}).`,
      ...(error?.request_id ? { requestId: error.request_id } : {}),
      details: error?.details,
    })
  }
  return body as HoldingsImportResult
}
